import { FC, useMemo, useState } from 'react';
import { Transition } from '@headlessui/react';
import { MailIcon, PlusSmIcon } from '@heroicons/react/outline';
import { useNavigate } from 'react-router-dom';

import {
  MailSequenceTemplateDataFragment,
  useMailSequenceDeleteTemplateMutation,
  useMailSequenceTemplatesQuery,
} from '../../../../generated/graphql';

import Button from '../../../components/Button';
import EmptyProfileTab from '../../../components/EmptyProfileTab';
import IllustrationMail from '../../../svgs/IllustrationMail';
import Modal from '../../../components/Modal';
import { customToast } from '../../../components/ToastAlert/customToast';
import { defaultTransitionProps } from '../../../lib/animation';
import Loading from '../../Loading';
import TemplateCard from './TemplateCard';

const MailSequenceTemplates: FC = () => {
  const navigate = useNavigate();
  const [templateToDelete, setTemplateToDelete] =
    useState<MailSequenceTemplateDataFragment | null>(null);

  const { data, loading, error, refetch } = useMailSequenceTemplatesQuery({
    fetchPolicy: 'cache-and-network',
    onError: (err) => {
      customToast.error(
        err.message || 'Could not load mail sequence templates.',
      );
    },
  });

  const [deleteTemplate, { loading: deleting }] =
    useMailSequenceDeleteTemplateMutation({
      onCompleted: ({ mailSequenceDeleteTemplate }) => {
        if (mailSequenceDeleteTemplate?.success) {
          customToast.success('Template deleted.');
          setTemplateToDelete(null);
          refetch();
        } else {
          customToast.error(
            mailSequenceDeleteTemplate?.error || 'Could not delete template.',
          );
        }
      },
      onError: (err) => {
        customToast.error(err.message || 'Could not delete template.');
      },
    });

  const templates = useMemo(
    () =>
      [...(data?.mailSequenceTemplates ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [data?.mailSequenceTemplates],
  );

  const openCreate = () => navigate('/automations/templates/mail-sequence/new');

  const openEdit = (template: MailSequenceTemplateDataFragment) =>
    navigate(`/automations/templates/mail-sequence/${template.id}/edit`);

  if (loading && !data) return <Loading />;

  return (
    <div className="flex w-full flex-col pb-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="mb-1 font-serif text-subtitle text-neutral-150">
            Mail sequence templates
          </h1>
        </div>
        <Button
          title="New Mail sequence template"
          IconComponent={PlusSmIcon}
          size="small"
          iconPosition="right"
          theme="primary"
          onClick={openCreate}
        />
      </div>

      <Transition show={!loading && !error} {...defaultTransitionProps}>
        {templates.length === 0 ? (
          <EmptyProfileTab
            IconComponent={IllustrationMail}
            iconClassName="text-secondary-100"
            titleText="No Mail sequence templates yet"
            subTitleText="Create a Mail sequence template to enroll clients into."
            onClickButton={openCreate}
            buttonTitle="New Mail sequence template"
            ButtonIconComponent={MailIcon}
          />
        ) : (
          <div className="flex flex-col gap-6">
            {templates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onEdit={() => openEdit(template)}
                onDelete={() => setTemplateToDelete(template)}
              />
            ))}
          </div>
        )}
      </Transition>

      <Modal
        isOpen={Boolean(templateToDelete)}
        setClosed={() => setTemplateToDelete(null)}
        width="small"
      >
        <div className="px-10 py-8">
          <div className="flex flex-col items-center">
            <div className="mb-2 flex text-center font-serif text-subtitle-small text-green-150">
              Delete template?
            </div>
            <div className="text-center text-body text-green-125">
              {templateToDelete
                ? `Existing Mail sequences built from “${templateToDelete.name}” keep their schedule. New enrollments into this sequence will no longer be possible.`
                : ''}
            </div>
          </div>
          <div className="mt-11 flex w-full flex-row items-center justify-center">
            <Button
              title="Cancel"
              theme="secondary"
              className="mr-5"
              onClick={() => setTemplateToDelete(null)}
              disabled={deleting}
            />
            <Button
              title={deleting ? 'Deleting...' : 'Delete'}
              theme="primary"
              disabled={deleting}
              onClick={() => {
                if (!templateToDelete) return;
                deleteTemplate({
                  variables: { templateId: templateToDelete.id },
                });
              }}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MailSequenceTemplates;
